import { TKey } from "@anton.bobrov/react-components";
import { TFormSelectGroup } from "@housebets/housebets-x-package/lib/components/form/Select/global";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { currencySelectOptions as CO } from "app/components/constants";
import {useAppSelector} from "hooks/useAppSelector";
import { setUserCurrency } from "app/store/slices/user";
import { CurrencyItem, CurrencyType } from "app/types/common";
import { getCurrencyIcon, toLocaleNumber } from "app/utils/help";
import { useIntl } from "react-intl";

export const useCryptoDropdownData = () => {
    const dispatch = useDispatch();

    const balance = useAppSelector((state) => state.user.balance);
    const userCurrency = useAppSelector((state) => state.user.userCurrency);
    const turnoverData = useAppSelector((state) => state.user.turnoverData);
    const isFiat = useAppSelector((state) => state.setting.isFiat);
    const hideZeroBalance = useAppSelector((state) => state.setting.hideZeroBalance);
    const currencyList = useAppSelector((state) => state.config.platformConfig?.currencyList);
    const [currencySelectOptions, setCurrencySelectOptions] = useState(CO);
    const {$t} = useIntl();

    const turnoverDict = useMemo(() => {
        return turnoverData
            .map((x: any) => {
                return {
                    type: x.type,
                    amount: x.amount,
                    wager: x.wager,
                };
            })
            .reduce((pre: any, cur: any) => {
                pre[cur.type] = cur;
                return pre;
            }, Object.create(null));
    }, [turnoverData]);

    const getBet = (item: any) => {
        const wager = Number(turnoverDict[item.tokenId]?.wager);
        const amount = Number(turnoverDict[item.tokenId]?.amount);

        if (wager < amount) {
            return {
                current: wager,
                max: amount.toFixed(2),
            };
        }
        return null;
    };

    useEffect(() => {
        const dataHandler = (list: any, maximumFractionDigits: number): TFormSelectGroup[] => {
            const res: any = [];
            list.forEach((v: any) => {
                const item = {
                    ...v,
                    id: v.tokenId,
                    name: v.tokenName,
                    value: toLocaleNumber(balance.find((x) => x.type === v.tokenId)?.amount, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits,
                        useGrouping: true,
                    }),
                    icon: getCurrencyIcon(v.tokenId),
                    bet: getBet(v),
                };
                res.push(item);
            });

            return res;
        };
        const CryptoList = (currencyList || []).filter((v: CurrencyItem) => v.categoryId === CurrencyType.Crypto);
        const FiatList = (currencyList || []).filter((v: CurrencyItem) => v.categoryId === CurrencyType.Fiat);
        setCurrencySelectOptions(prev => {
            return prev.map((v) => {
                if (v.id === CurrencyType.Crypto) {
                    return {
                        ...v,
                        name: $t({ id: v.name }),
                        options: dataHandler(CryptoList, 6)
                    }
                }
                if (v.id === CurrencyType.Fiat) {
                    return {
                        ...v,
                        name: $t({ id: v.name }),
                        options: dataHandler(FiatList, 2)
                    }
                }

                return v;
            })
        })
    }, [balance, userCurrency, currencyList])

    const [options, setOptions] = useState<TFormSelectGroup[]>(
        currencySelectOptions,
    );

    const handleSelected = ((ids: TKey[]) => {
        dispatch(setUserCurrency(ids[0]))
    });


    const getUsd = (item: any) => {
        const rate = balance.find((v) => v.type === item.tokenId)?.rate || 0;
        return toLocaleNumber(Number(item.value.replace(/,/g, "")) * rate, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: true,
        });
    };

    useEffect(() => {
        let mapped = currencySelectOptions;

        if (mapped && isFiat) {
            mapped = mapped.map((item) => {
                return {
                    ...item,
                    name: $t({ id: item.name }),
                    options: item?.options.map((opt) => ({
                        ...opt,
                        value: `$${getUsd(opt)}`,
                    })),
                };
            });
        }

        if (mapped && hideZeroBalance) {
            mapped = mapped.map((item: any) => {
                return {
                    ...item,
                    options: item?.options.filter((item: any) => parseFloat((item.value || "").replace("$", "")) !== 0),
                };
            });
        }

        setOptions(mapped);
    }, [currencySelectOptions, isFiat, hideZeroBalance]);

    return {
        isFiat,
        hideZeroBalance,
        userCurrency,
        handleSelected,
        options
    }
}